import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { money, calcCommission, effectiveRate } from "../lib/finance";
import { findRenewedIds, isLost } from "../lib/renewal";
import { CompanyLogo } from "../lib/companies";

const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const NONE = "(Belirtilmemiş)";
const fmtDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString("tr-TR") : "—");

function useDetail(company, policies, branches, settings, year) {
  return useMemo(() => {
    if (!company) return null;
    const comm = (p) => calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal;
    const mine = policies.filter((p) => !p.deletedAt && ((p.company || "").trim() || NONE) === company);
    const now = new Date();
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
      return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, prim: 0, komisyon: 0 };
    });
    const byKey = Object.fromEntries(months.map((m) => [m.key, m]));
    mine.forEach((p) => { const m = byKey[String(p.start || "").slice(0, 7)]; if (m) { m.prim += Number(p.premium || 0); m.komisyon += comm(p); } });
    const today = now.toISOString().slice(0, 10);
    const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString().slice(0, 10);
    const renewed = findRenewedIds(policies);
    const due = mine.filter((p) => (p.status || "aktif") !== "iptal" && p.end && p.end <= today && p.end >= yearAgo);
    const renewedCount = due.filter((p) => renewed.has(p.id)).length;
    const lost = mine.filter(isLost);
    const rows = mine.filter((p) => p.start && new Date(p.start).getFullYear() === year).sort((a, b) => String(b.start).localeCompare(String(a.start)));
    return {
      months, rows, due: due.length, renewedCount, lost: lost.length, lostPremium: lost.reduce((s, p) => s + Number(p.premium || 0), 0),
      prim: rows.reduce((s, p) => s + Number(p.premium || 0), 0), komisyon: rows.reduce((s, p) => s + comm(p), 0), comm,
    };
  }, [company, policies, branches, settings, year]);
}

function Kpis({ d, year }) {
  const rate = d.due ? Math.round((d.renewedCount / d.due) * 100) : null;
  return (
    <div className="cd-kpis">
      <div data-testid="cd-kpi-prim"><small>{year} prim</small><strong>{money(d.prim)}</strong><em>{d.rows.length} poliçe</em></div>
      <div data-testid="cd-kpi-commission"><small>{year} komisyon</small><strong>{money(d.komisyon)}</strong><em>%{d.prim ? ((d.komisyon / d.prim) * 100).toFixed(1) : "0"} oran</em></div>
      <div data-testid="cd-kpi-renewal"><small>Yenileme oranı</small><strong>{rate == null ? "—" : `%${rate}`}</strong><em>{d.renewedCount}/{d.due} vadesi gelen · son 12 ay</em></div>
      <div data-testid="cd-kpi-lost"><small>Kaybedilen</small><strong>{d.lost}</strong><em>{money(d.lostPremium)} prim</em></div>
    </div>
  );
}

export function CompanyDetailSheet({ company, onClose, policies, branches, settings, year }) {
  const d = useDetail(company, policies, branches, settings, year);
  return (
    <Sheet open={!!company} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="company-detail-sheet" data-testid="company-detail-sheet">
        {d && (
          <>
            <SheetHeader>
              <SheetTitle className="cd-title">{company !== NONE && <CompanyLogo name={company} size={34} />}<span data-testid="cd-company-name">{company}</span></SheetTitle>
              <SheetDescription>Şirket performans detayı · {year}</SheetDescription>
            </SheetHeader>
            <Kpis d={d} year={year} />
            <div className="cd-section">
              <strong>Son 12 ay prim / komisyon</strong>
              <div className="cd-chart" data-testid="cd-monthly-chart">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={d.months} margin={{ left: -10, right: 4 }}>
                    <CartesianGrid vertical={false} stroke="#e8edf3" />
                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-35} textAnchor="end" height={40} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}K` : v)} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="prim" name="Prim" fill="var(--navy)" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="komisyon" name="Komisyon" fill="#1a5fe0" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="cd-section">
              <strong>{year} poliçeleri ({d.rows.length})</strong>
              <div className="cd-list" data-testid="cd-policy-list">
                {d.rows.length === 0 && <em className="dim">Bu yıl poliçe yok</em>}
                {d.rows.map((p) => (
                  <div key={p.id} className="cd-policy" data-testid={`cd-policy-${p.id}`}>
                    <div><strong>{p.customer}</strong><span>{p.branch}{p.plate ? ` · ${p.plate}` : ""} · {fmtDate(p.start)} — {fmtDate(p.end)}</span></div>
                    <div className="cd-policy-amt"><strong>{money(p.premium)}</strong><em>{money(d.comm(p))}</em></div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
